import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, RefreshCw, ArrowLeftRight, ChevronRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useWalletStore } from '@/stores/useWalletStore'
import { usePairsStore } from '@/stores/usePairsStore'
import { useConfigStore } from '@/stores/useConfigStore'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { CommonFooter } from '@/components/layout'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { TokenRow } from '@/components/token/TokenRow'
import { TokenSelector } from '@/components/token/TokenSelector'
import { getPair, getPath } from '@/lib/swap-lib'
import { toFixed, decdiv, decmul, decsub } from '@/lib/decimal'
import { sleep } from '@/lib/common'
import { eosTool } from '@/services/blockchain/eos-tool'
import type { TokenInfo, Pair } from '@/types'

import swapHeader from '@/assets/images/swap-hader.png'

interface PairState {
  pay: TokenInfo | null
  receive: TokenInfo | null
  pairId: number | null
}

export function SwapPage() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const { isLogin, session } = useWalletStore()
  const { pairs, coins, lastPair, setLastPair, fetchPairs } = usePairsStore()
  const { slippageTolerance, isMultipath, config } = useConfigStore()
  const { transact, loading: txLoading } = useEosTransaction()

  const [pair, setPair] = useState<PairState>({ pay: null, receive: null, pairId: null })
  const [payAmount, setPayAmount] = useState('')
  const [receiveAmount, setReceiveAmount] = useState('')
  const [balance0, setBalance0] = useState<string | null>(null)
  const [balance1, setBalance1] = useState<string | null>(null)
  const [swapPrice, setSwapPrice] = useState('')
  const [priceDirection, setPriceDirection] = useState(0)
  const [premiumRate, setPremiumRate] = useState('')
  const [bestPairPath, setBestPairPath] = useState<Pair[]>([])
  const [bestTokenPath, setBestTokenPath] = useState<TokenInfo[]>([])
  const [selectorType, setSelectorType] = useState<'pay' | 'receive' | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const balanceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fee = useMemo(() => parseFloat(config.totalFeeFloat) || 0.003, [config.totalFeeFloat])

  const displayPrice = useMemo(() => {
    const p = parseFloat(payAmount)
    const r = parseFloat(receiveAmount)
    if (p > 0 && r > 0) return decdiv(receiveAmount, payAmount)
    return swapPrice
  }, [payAmount, receiveAmount, swapPrice])

  // Initialize pair from lastPair or default
  useEffect(() => {
    if (pairs.length === 0) return
    if (pair.pay && pair.receive) return

    let pay: TokenInfo | null = null
    let receive: TokenInfo | null = null

    if (lastPair) {
      pay = lastPair.token0
      receive = lastPair.token1
    } else {
      pay = coins.find((c) => c.tokenId === 'eosio.token-EOS') || coins[0] || null
      receive = coins.find((c) => c.tokenId === 'tethertether-USDT') || coins[1] || null
    }

    if (pay && receive) {
      const foundPair = getPair(pay, receive, pairs)
      setPair({ pay, receive, pairId: foundPair?.id ?? null })
    }
  }, [pairs, coins, lastPair])

  // Fetch balances
  const fetchBalances = useCallback(async () => {
    if (!pair.pay || !pair.receive) {
      setBalance0(null)
      setBalance1(null)
      return
    }
    if (!isLogin || !session) {
      setBalance0(toFixed(0, pair.pay.precision))
      setBalance1(toFixed(0, pair.receive.precision))
      return
    }
    const account = session.account.name
    try {
      const [b0, b1] = await Promise.all([
        eosTool.getCurrencyBalance(pair.pay.contract, account, pair.pay.coin),
        eosTool.getCurrencyBalance(pair.receive.contract, account, pair.receive.coin),
      ])
      setBalance0(b0 ? b0.split(' ')[0] : '0')
      setBalance1(b1 ? b1.split(' ')[0] : '0')
    } catch { /* silently fail */ }
  }, [isLogin, session, pair.pay, pair.receive])

  useEffect(() => {
    fetchBalances()
    if (balanceTimerRef.current) clearInterval(balanceTimerRef.current)
    balanceTimerRef.current = setInterval(fetchBalances, 6000)
    return () => { if (balanceTimerRef.current) clearInterval(balanceTimerRef.current) }
  }, [fetchBalances])

  // Calculate price when pair changes
  useEffect(() => {
    if (pair.pay && pair.receive && pairs.length > 0) {
      handleGetPrice()
      if (payAmount) handleInputPrice(payAmount, 1)
    }
  }, [pair.pay, pair.receive, pairs])

  const handleGetPrice = () => {
    if (!pair.pay || !pair.receive) return
    const result = getPath(pair.pay.tokenId, pair.receive.tokenId, 0, fee, pairs, 1, isMultipath)
    if (result.bestAmount > 0) {
      setSwapPrice(toFixed(result.bestAmount, 8))
      setBestPairPath(result.bestPairPath)
      setBestTokenPath(result.bestTokenPath)
    } else {
      setSwapPrice('')
      setBestPairPath([])
      setBestTokenPath([])
    }
  }

  const handleInputPrice = (amount: string, way: 1 | 2) => {
    if (!pair.pay || !pair.receive) return
    const num = parseFloat(amount)
    if (!amount || isNaN(num) || num === 0) {
      if (way === 1) setReceiveAmount('')
      else setPayAmount('')
      setPremiumRate('')
      setBestPairPath([])
      setBestTokenPath([])
      return
    }

    const result = getPath(
      way === 1 ? pair.pay.tokenId : pair.receive.tokenId,
      way === 1 ? pair.receive.tokenId : pair.pay.tokenId,
      num, fee, pairs, way, isMultipath,
    )

    if (result.bestAmount > 0) {
      const precision = way === 1 ? pair.receive.precision : pair.pay.precision
      const formatted = toFixed(result.bestAmount, precision)
      if (way === 1) setReceiveAmount(formatted)
      else setPayAmount(formatted)

      setBestPairPath(result.bestPairPath)
      setBestTokenPath(result.bestTokenPath)

      if (swapPrice && num > 0) {
        const idealOutput = way === 1 ? parseFloat(decmul(num, swapPrice)) : parseFloat(decdiv(num, swapPrice))
        if (idealOutput > 0) {
          const impact = parseFloat(decsub(1, decdiv(result.bestAmount, idealOutput)))
          setPremiumRate(toFixed(Math.abs(impact) * 100, 2))
        }
      }
    } else {
      if (way === 1) setReceiveAmount('')
      else setPayAmount('')
      setPremiumRate('')
    }
  }

  const handlePayInput = (value: string) => {
    setPayAmount(value)
    handleInputPrice(value, 1)
  }

  const handleReceiveInput = (value: string) => {
    setReceiveAmount(value)
    handleInputPrice(value, 2)
  }

  const handleChangeCoin = () => {
    setPair((prev) => ({ pay: prev.receive, receive: prev.pay, pairId: prev.pairId }))
    setPayAmount(receiveAmount)
    setReceiveAmount(payAmount)
    setBalance0(balance1)
    setBalance1(balance0)
  }

  const handleTokenSelect = (token: TokenInfo) => {
    setPair((prev) => {
      const newPair = { ...prev }
      if (selectorType === 'pay') {
        if (prev.receive?.tokenId === token.tokenId) {
          newPair.pay = prev.receive; newPair.receive = prev.pay
        } else { newPair.pay = token }
      } else {
        if (prev.pay?.tokenId === token.tokenId) {
          newPair.pay = prev.receive; newPair.receive = prev.pay
        } else { newPair.receive = token }
      }
      const foundPair = getPair(newPair.pay!, newPair.receive!, pairs)
      newPair.pairId = foundPair?.id ?? null
      return newPair
    })
    setPayAmount('')
    setReceiveAmount('')
    setPremiumRate('')
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchPairs()
    handleGetPrice()
    if (payAmount) handleInputPrice(payAmount, 1)
    setRefreshing(false)
  }

  const minReceived = useMemo(() => {
    if (!receiveAmount || !pair.receive) return ''
    const amount = parseFloat(receiveAmount)
    const min = amount * (1 - slippageTolerance / 100)
    return toFixed(min, pair.receive.precision)
  }, [receiveAmount, slippageTolerance, pair.receive])

  const buildSwapMemo = (): string => {
    if (!pair.receive || bestPairPath.length === 0) return ''
    const pathIds = bestPairPath.map((p) => p.id).join('-')
    const minAmount = parseFloat(minReceived)
    const minInt = Math.floor(minAmount * Math.pow(10, pair.receive.precision))
    return `swap:${pathIds},min:${minInt}`
  }

  const handleSubmit = async () => {
    if (!pair.pay || !pair.receive || !session || !payAmount) return
    const payNum = parseFloat(payAmount)
    if (payNum <= 0) return
    if (balance0 && payNum > parseFloat(balance0)) return

    const account = session.account
    const quantity = `${toFixed(payNum, pair.pay.precision)} ${pair.pay.coin}`
    const memo = buildSwapMemo()

    const actions = [{
      account: pair.pay.contract,
      name: 'transfer',
      authorization: [{ actor: account.name, permission: account.authority || 'active' }],
      data: { from: account.name, to: 'swap.ttr', quantity, memo },
    }]

    const result = await transact(actions)
    if (result) {
      const foundPair = getPair(pair.pay, pair.receive, pairs)
      if (foundPair) setLastPair(foundPair)
      setPayAmount('')
      setReceiveAmount('')
      setPremiumRate('')
      await sleep(500)
      await fetchPairs()
      fetchBalances()
    }
  }

  const isInsufficientBalance = payAmount && balance0 && parseFloat(payAmount) > parseFloat(balance0)

  const buttonText = () => {
    if (!isLogin) return 'Connect wallet'
    if (!pair.pay || !pair.receive) return t('swap.selectToken')
    if (!payAmount) return t('swap.swap')
    if (isInsufficientBalance) return t('swap.insufficientBalance')
    return t('swap.swap')
  }

  const isButtonDisabled = !isLogin || !pair.pay || !pair.receive || !payAmount || !!isInsufficientBalance || txLoading || bestPairPath.length === 0

  return (
    <div className="w-full animate-trade-enter">
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[400px]">
        {/* Totoro header image */}
        {!isMobile && (
          <div className="flex justify-center">
            <img src={swapHeader} className="w-[83px] h-[103px]" alt="" />
          </div>
        )}

        {/* Swap card */}
        <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
          {/* Nav: refresh */}
          <div className="flex justify-end items-center pb-[20px] border-b border-[#ddf5ff] mb-[16px]">
            <button
              onClick={handleRefresh}
              className="p-1 cursor-pointer"
              disabled={refreshing}
            >
              <RefreshCw className={`h-[20px] w-[20px] text-[#999] ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Pay token row */}
          <TokenRow
            token={pair.pay}
            amount={payAmount}
            balance={balance0}
            onTokenClick={() => setSelectorType('pay')}
            onInput={handlePayInput}
            onBalanceClick={() => { if (balance0) handlePayInput(balance0) }}
          />

          {/* Swap direction button */}
          <div className="flex justify-center my-[18px]">
            <button
              onClick={handleChangeCoin}
              className="w-[42px] h-[42px] rounded-full bg-[#498846] flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
            >
              <ArrowDown className="h-5 w-5 text-white" />
            </button>
          </div>

          {/* Receive token row */}
          <TokenRow
            token={pair.receive}
            amount={receiveAmount}
            balance={balance1}
            onTokenClick={() => setSelectorType('receive')}
            onInput={handleReceiveInput}
          />

          {/* Price info */}
          {pair.pay && pair.receive && swapPrice && bestPairPath.length > 0 && (
            <div className="mt-[10px] py-[5px] text-[13px]">
              {/* Price */}
              <div className="flex items-center justify-between leading-[20px]">
                <span className="text-[#666]">Price</span>
                <button
                  onClick={() => setPriceDirection(Math.abs(priceDirection - 1))}
                  className="text-[#333] flex items-center gap-1 cursor-pointer"
                >
                  {priceDirection === 0 ? (
                    <span>1 {pair.pay.coin} ≈ {toFixed(parseFloat(displayPrice), 8)} {pair.receive.coin}</span>
                  ) : (
                    <span>1 {pair.receive.coin} ≈ {toFixed(parseFloat(decdiv(1, displayPrice)), 8)} {pair.pay.coin}</span>
                  )}
                  <ArrowLeftRight className="h-3 w-3 text-[#666]" />
                </button>
              </div>

              {/* Slippage / price impact */}
              {premiumRate && parseFloat(premiumRate) !== 0 && payAmount && (
                <div className="flex items-center justify-between leading-[20px] mt-[3px]">
                  <span className="text-[#666]">{t('swap.priceImpact')}</span>
                  <span className={
                    parseFloat(premiumRate) <= 1 ? 'text-[#00be66]' :
                    parseFloat(premiumRate) <= 5 ? 'text-[#feb75e]' : 'text-red-500'
                  }>
                    {parseFloat(premiumRate) < 0.01 ? '< ' : ''}{premiumRate}%
                  </span>
                </div>
              )}

              {/* Route */}
              {bestTokenPath.length > 2 && (
                <div className="flex items-center justify-between leading-[20px] mt-[3px]">
                  <span className="text-[#666]">{t('swap.route')}</span>
                  <span className="text-[#333]">{bestTokenPath.map((t) => t.coin).join(' → ')}</span>
                </div>
              )}
            </div>
          )}

          {/* No pool message */}
          {pair.pay && pair.receive && bestPairPath.length === 0 && swapPrice === '' && (
            <div className="text-center py-[40px] text-[12px]">
              <div className="text-[#666]">{t('swap.noPool')}</div>
              <Link to="/pool" className="text-primary mt-[20px] block">{t('swap.addLiquidity')} &gt;</Link>
            </div>
          )}

          {/* Swap button */}
          <div className="mt-[21px]">
            <button
              onClick={handleSubmit}
              disabled={isButtonDisabled}
              className="w-full h-[48px] rounded-[24px] bg-[#498846] text-white text-[18px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95 transition-opacity flex items-center justify-center"
            >
              {txLoading ? t('public.loading') : buttonText()}
            </button>
          </div>
        </div>

        {/* Swap record link */}
        <div className="flex justify-end mt-[30px]">
          <Link to="/swap/logs" className="text-[13px] text-primary font-semibold flex items-center gap-0.5">
            {t('swap.swapRecord')}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Token Selector */}
      <TokenSelector
        open={selectorType !== null}
        onClose={() => setSelectorType(null)}
        onSelect={handleTokenSelect}
        coins={coins}
        selected={selectorType === 'pay' ? pair.pay : pair.receive}
      />
    </div>
    <CommonFooter />
    </div>
  )
}
